import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import usePageTracking from '../hooks/usePageTracking';
import Header from './Header';
import SignaturePad from './SignaturePad';
import api from '../api/axios';

const Layout = ({ children }) => {
    // Función para limpiar siempre la caché de imágenes
    const [cacheBuster] = useState(() => Date.now());
    const getCleanImageUrl = (url) => {
        if (!url) return '';
        return `${url}${url.includes('?') ? '&' : '?'}v=${cacheBuster}`;
    };

    const { logout, user, updateUser } = useAuth();
    const { config } = useConfig();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [signatureData, setSignatureData] = useState(null);
    const [signatureAccepted, setSignatureAccepted] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);
    const [sigError, setSigError] = useState('');

    const [showSalaModal, setShowSalaModal] = useState(false);
    const [salas, setSalas] = useState([]);
    const [selectedSalaId, setSelectedSalaId] = useState('');
    const [isSubmittingSala, setIsSubmittingSala] = useState(false);
    const [salaError, setSalaError] = useState('');

    usePageTracking();

    useEffect(() => {
        if (!user || (user.role !== 'staff' && user.role !== 'admin') || user.sala_id) {
            setShowSalaModal(false);
            return;
        }

        const checkSalas = async () => {
            try {
                const res = await api.get('/salas');
                if (res.data && res.data.length > 0) {
                    setSalas(res.data);
                    setShowSalaModal(true);
                } else {
                    setShowSalaModal(false);
                }
            } catch (err) {
                console.error("Error checking salas in Layout", err);
            }
        };

        checkSalas();
    }, [user]);

    const handleSaveSala = async () => {
        if (!selectedSalaId) {
            setSalaError('Por favor selecciona una sala para continuar.');
            return;
        }
        setIsSubmittingSala(true);
        setSalaError('');
        try {
            const res = await api.put('/users/profile/update', { sala_id: selectedSalaId });
            updateUser(res.data.user);
            setShowSalaModal(false);
        } catch (err) {
            console.error("Error updating staff sala", err);
            setSalaError('Error al asignar la sala, por favor intenta de nuevo.');
        } finally {
            setIsSubmittingSala(false);
        }
    };

    useEffect(() => {
        // Deshabilitado requerimiento de firma
        setShowSignatureModal(false);
    }, [user]);

    const handleSaveSignature = async () => {
        const regFields = config?.registration_fields || {};
        if (regFields.terms_accepted && !termsAccepted) {
            setSigError('Debes aceptar los términos y condiciones antes de guardar.');
            return;
        }
        if (!signatureAccepted) {
            setSigError('Debes aceptar la autorización de uso de datos antes de guardar.');
            return;
        }
        if (!signatureData) {
            setSigError('Por favor dibuja tu firma.');
            return;
        }
        setIsSubmittingSignature(true);
        setSigError('');
        try {
            const res = await api.put('/users/profile/update', { signature: signatureData });
            updateUser(res.data.user);
            setShowSignatureModal(false);
            navigate('/profile');
        } catch (err) {
            console.error("Error updating signature from layout modal", err);
            setSigError('Error al guardar la firma, por favor intenta de nuevo.');
        } finally {
            setIsSubmittingSignature(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isStaff = user?.role === 'staff' || user?.role === 'admin';

    const getPageKey = () => {
        const path = location.pathname;
        if (path === '/' || path === '') return 'home';
        if (path.includes('agenda')) return 'agenda';
        if (path.includes('speakers')) return 'speakers';
        if (path.includes('streaming')) return 'streaming';
        if (path.includes('gallery')) return 'gallery';
        if (path.includes('profile')) return 'profile';
        if (path.includes('login')) return 'login';
        if (path.includes('register')) return 'register';
        if (path.includes('welcome')) return 'welcome';
        if (path.includes('thanks')) return 'thanks';
        if (path.includes('certificate')) return 'certificate';
        if (path.includes('questions-expert')) return 'questions_private';
        if (path.includes('interactiva-stats')) return 'interactiva-stats';
        if (path.includes('interactiva')) return 'interactiva';
        if (path.includes('bingo')) return 'bingo';
        return 'home';
    };

    const pageKey = getPageKey();
    const pageDesign = config?.pageDesigns?.[pageKey] || { enabled: true, bgImage: '', showBrain: true };

    const navItems = (isStaff
        ? [
            { path: '/', label: 'INICIO', icon: '🏠', key: 'home' },
            { path: '/agenda', label: 'AGENDA', icon: '📅', key: 'agenda' },
            { path: '/scanner', label: 'ESCANEAR', icon: '📷', key: 'scanner' },
            { path: '/staff-questions', label: 'MODERAR Q&A', icon: '👨‍⚖️', key: 'questions_private' },
            { path: '/questions-expert', label: 'PREGUNTAR', icon: '💬', key: 'questions_private' },
            { path: '/interactiva-gestion', label: 'GESTIÓN TRIVIA', icon: '⚙️', key: 'interactiva' },
            { path: '/interactiva-stats/0', label: 'MÉTRICAS TRIVIA', icon: '📊', key: 'interactiva' },
            { path: '/staff-gallery', label: 'CARGAR FOTOS', icon: '📸', key: 'gallery' },
            { path: '/profile', label: 'PERFIL', icon: '👤', key: 'profile' },
        ]
        : [
            { path: '/', label: 'INICIO', icon: '🏠', key: 'home' },
            { path: '/agenda', label: 'AGENDA', icon: '📅', key: 'agenda' },
            { path: '/welcome', label: 'BIENVENIDA', icon: '📜', key: 'welcome' },
            { path: '/speakers', label: 'PONENTES', icon: '🎙️', key: 'speakers' },
            ...(config?.encuesta_config?.enabled !== false ? [{
                path: '/survey',
                label: config?.encuesta_config?.title || 'ENCUESTA',
                icon: '📝',
                key: 'encuesta',
                type: 'internal'
            }] : []),
            { path: '/streaming', label: 'EN VIVO', icon: '📺', key: 'streaming' },
            { path: '/questions-expert', label: 'PREGUNTAR', icon: '💬', key: 'questions_private' },
            {
                path: '/event-gallery',
                label: 'GALERÍA',
                icon: '🖼️',
                key: 'gallery',
                type: config?.pageDesigns?.gallery?.type,
                url: config?.pageDesigns?.gallery?.url
            },
            ...(config?.customGalleryPages || []).map(p => ({
                path: `/gallery/${p.slug}`,
                label: p.title.toUpperCase(),
                icon: p.icon || '🖼️',
                key: 'gallery',
                type: p.type,
                url: p.url
            })),
            { path: '/certificate', label: 'CERTIFICADO', icon: '🎓', key: 'certificate' },
            { path: '/thanks', label: 'AGRADECIMIENTO', icon: '🙏', key: 'thanks' },
            { path: '/interactiva', label: 'TRIVIA EN VIVO', icon: '🗳️', key: 'interactiva' },
            { path: '/profile', label: 'MI PERFIL', icon: '👤', key: 'profile' },
            ...(user?.role === 'admin' ? [{ path: '/scanner', label: 'Escanear', icon: '📷', key: 'scanner' }] : []),
        ]).filter(item => {
            if (!item.key) return true;
            const design = config?.pageDesigns?.[item.key];
            return design ? design.enabled !== false : true;
        });

    const isAgendaImageOnly = pageKey === 'agenda' && config?.pageDesigns?.agenda?.agendaImageOnly;
    // Precise check for gallery embed: check custom pages AND the main gallery page
    const currentGalleryPage = (config?.customGalleryPages || []).find(p => location.pathname.includes(p.slug));
    const isMainGalleryEmbed = pageKey === 'gallery' && location.pathname.includes('event-gallery') && config?.pageDesigns?.gallery?.type === 'embed';
    const isGalleryEmbed = (pageKey === 'gallery' && currentGalleryPage?.type === 'embed') || isMainGalleryEmbed;
    const hideHeaderFooter = isAgendaImageOnly || isGalleryEmbed;
    const hideGlobalFooter = hideHeaderFooter || pageKey === 'agenda';
    const hideBottomBg = ['agenda', 'speakers', 'interactiva', 'questions_private'].includes(pageKey);

    const bottomBg = isMobile
        ? getCleanImageUrl('https://ftp.pcnpublicidad.com/adium/fondo_movil.png')
        : getCleanImageUrl('https://ftp.pcnpublicidad.com/adium/fondo_desktop.png');

    const isCustomBg = config?.backgroundImage && !config.backgroundImage.includes('fondo.png');
    const defaultBg = pageKey === 'speakers'
        ? '/assets/cardio/CS_Fondo-Largo.png'
        : (isMobile ? '/assets/cardio/CS_Fondo-vertical.png' : '/assets/cardio/CS_Fondo-Horizontal.png');

    const bgImage = isCustomBg
        ? (isMobile ? (config?.backgroundMobile || config?.backgroundImage) : (config?.backgroundDesktop || config?.backgroundImage))
        : defaultBg;

    const containerStyle = {
        backgroundColor: '#550043',
        color: pageDesign.globalFontColor || config?.globalFontColor || '#ffffff',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: pageKey === 'speakers' ? 'cover' : 'cover',
        backgroundPosition: 'top center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: pageKey === 'speakers' ? 'scroll' : 'fixed',
    };

    return (
        <div className="min-h-screen flex flex-col relative overflow-x-hidden transition-all duration-700 font-sans" style={containerStyle}>
            {/* Bottom Background Layer has been moved inline to Home and Login pages */}

            {/* Removed dynamic visual elements (pageDesign.elements) to prevent old database texts from overlapping with static images */}

            {!hideHeaderFooter && pageKey !== 'bingo' && pageKey !== 'bingo-staff' && <Header
                pageKey={pageKey}
                showBrain={pageDesign.showBrain}
                title={pageDesign.title}
                subtitle={pageDesign.subtitle}
                subtitleType={pageDesign.subtitleType}
                subtitleImage={pageDesign.subtitleImage}
            />}

            <main className={`flex-grow w-full relative z-10 
                ${(pageKey === 'interactiva-stats' || pageKey === 'gallery' || pageKey === 'bingo') ? 'max-w-none px-0' : 'max-w-4xl px-4 sm:px-6 mx-auto'} 
                ${(hideHeaderFooter || pageKey === 'bingo') ? 'p-0 min-h-full' : 'py-6'}`}>
                {children}
            </main>


            {/* Hamburger Button (Top Right) */}
            {user && (
                <div className="fixed top-4 right-4 sm:top-6 sm:right-6 z-[110] animate-fadeIn">
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-label="Menú"
                        className={`w-12 h-12 flex flex-col items-center justify-center gap-1.5 transition-all duration-500 z-[110] ${isMenuOpen ? 'rotate-90 scale-110' : ''}`}
                    >
                        {!isMenuOpen ? (
                            <>
                                <span className="w-8 h-[3.5px] rounded-full shadow-sm" style={{ backgroundColor: '#f61170' }}></span>
                                <span className="w-8 h-[3.5px] rounded-full shadow-sm" style={{ backgroundColor: '#f61170' }}></span>
                                <span className="w-8 h-[3.5px] rounded-full shadow-sm" style={{ backgroundColor: '#f61170' }}></span>
                            </>
                        ) : (
                            <span className="text-3xl font-black" style={{ color: '#f61170' }}>✕</span>
                        )}
                    </button>
                </div>
            )}

            {/* Navigation Side Drawer */}
            <div className={`fixed inset-0 z-[100] transition-all duration-500 ${isMenuOpen ? 'visible' : 'invisible'
                }`}>
                {/* Backdrop Overlay */}
                <div
                    className={`absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-500 ${isMenuOpen ? 'opacity-100' : 'opacity-0'
                        }`}
                    onClick={() => setIsMenuOpen(false)}
                ></div>

                {/* Sidebar Panel */}
                <div
                    style={{
                        background: config?.sidebarBgColor
                            ? `linear-gradient(135deg, ${config.sidebarBgColor}f7, ${config.sidebarBgColor}cc)`
                            : 'linear-gradient(135deg, rgba(127,29,29,0.97), rgba(192,57,43,0.97), rgba(231,76,60,0.97))'
                    }}
                    className={`absolute top-0 right-0 w-[260px] sm:w-[300px] h-full backdrop-blur-2xl border-l border-white/10 shadow-[-20px_0_50px_rgba(0,0,0,0.5)] transition-transform duration-500 transform ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'
                        }`}
                >
                    <div className="flex flex-col h-full p-8 pt-24">
                        <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
                            <div className="space-y-3">
                                {navItems.map((item, index) => (
                                    <NavLink
                                        key={item.path}
                                        to={item.path}
                                        onClick={(e) => {
                                            if (item.type === 'redirect' && item.url) {
                                                e.preventDefault();
                                                window.open(item.url, '_blank');
                                                setIsMenuOpen(false);
                                                return;
                                            }
                                            setIsMenuOpen(false);
                                        }}
                                        style={{
                                            transitionDelay: `${index * 30}ms`,
                                            color: config?.sidebarFontColor || '#ffffff'
                                        }}
                                        className={({ isActive }) =>
                                            `flex items-center p-4 rounded-2xl border border-white/5 transition-all duration-300 transform ${isMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
                                            } ${isActive && item.type !== 'redirect'
                                                ? 'bg-black/20 shadow-lg border-black/10'
                                                : 'bg-black/5 hover:bg-black/10 border-black/5'
                                            }`
                                        }
                                    >
                                        <span className="text-xl mr-4">{item.icon}</span>
                                        <span className="text-xs font-black uppercase tracking-widest">{item.label}</span>
                                    </NavLink>
                                ))}
                            </div>
                        </div>

                        {/* Footer Section in Sidebar */}
                        <div className="mt-8 pt-8 border-t border-black/10 flex flex-col gap-4">
                            <div className="flex items-center p-4 rounded-2xl bg-black/5 border border-black/5">
                                <div className="w-10 h-10 rounded-full bg-black/80 flex items-center justify-center text-white font-black mr-3 shadow-lg">
                                    {user?.name?.[0].toUpperCase()}
                                </div>
                                <div className="overflow-hidden">
                                    <p className="text-xs font-black text-black truncate uppercase tracking-widest">{user?.name}</p>
                                    <p className="text-[9px] text-black/70 uppercase tracking-widest leading-none">Mi Perfil</p>
                                </div>
                            </div>

                            <button
                                onClick={handleLogout}
                                className="flex items-center justify-center gap-3 w-full py-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-200 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-red-500/20 transition-all"
                            >
                                <span>🚪</span> Cerrar Sesión
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showSignatureModal && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 p-4 animate-fadeIn">
                    <div className="bg-slate-900 border border-white/10 rounded-[40px] p-8 max-w-md w-full space-y-6 shadow-2xl relative">
                        <div className="text-center space-y-2">
                            <h3 className="text-2xl font-black text-white italic uppercase tracking-tighter">Ingrese Su Firma Para Continuar</h3>
                            <p className="text-white/60 text-xs leading-relaxed">
                                Para continuar disfrutando del evento, es requerido registrar tu firma de asistencia.
                            </p>
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer group">
                            <div className="relative flex items-center justify-center mt-1">
                                <input
                                    type="checkbox"
                                    className="peer sr-only"
                                    checked={signatureAccepted}
                                    onChange={(e) => {
                                        setSignatureAccepted(e.target.checked);
                                        if (!e.target.checked) setSignatureData(null);
                                    }}
                                />
                                <div className="w-5 h-5 rounded-md border-2 border-purple-500/50 bg-white/5 peer-checked:bg-purple-600 peer-checked:border-purple-600 transition-all flex items-center justify-center group-hover:border-purple-400 shadow-inner">
                                    <svg className={`w-3 h-3 text-white transform transition-transform ${signatureAccepted ? 'scale-100' : 'scale-0'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </div>
                            <span className="text-[10px] sm:text-xs text-white/70 font-medium leading-snug select-none group-hover:text-white transition-colors text-left">
                                Para validar su registro de ingreso al evento por favor firme aquí.
                            </span>
                        </label>

                        {signatureAccepted && (
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-4 animate-fadeIn">
                                <label className="block text-[8px] uppercase font-black tracking-[0.3em] text-white/40 mb-2 text-center">Dibuja tu firma aquí</label>
                                <SignaturePad onSignatureChange={(sig) => setSignatureData(sig)} />
                            </div>
                        )}

                        {config?.registration_fields?.terms_accepted && (
                            <label className="flex items-start gap-3 cursor-pointer group mt-4">
                                <div className="relative flex items-center justify-center mt-1">
                                    <input
                                        type="checkbox"
                                        className="peer sr-only"
                                        checked={termsAccepted}
                                        onChange={(e) => setTermsAccepted(e.target.checked)}
                                    />
                                    <div className="w-5 h-5 rounded-md border-2 border-purple-500/50 bg-white/5 peer-checked:bg-purple-600 peer-checked:border-purple-600 transition-all flex items-center justify-center group-hover:border-purple-400 shadow-inner">
                                        <svg className={`w-3 h-3 text-white transform transition-transform ${termsAccepted ? 'scale-100' : 'scale-0'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                </div>
                                <span className="text-[10px] sm:text-xs text-white/70 font-medium leading-snug select-none group-hover:text-white transition-colors text-left">
                                    Acepto los{' '}
                                    <a
                                        href={config.registration_fields.terms_url || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-purple-400 hover:text-purple-300 underline underline-offset-2"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        términos y condiciones
                                    </a>
                                    {' '}y políticas de privacidad.
                                </span>
                            </label>
                        )}

                        {sigError && (
                            <p className="text-red-400 text-[10px] font-bold text-center uppercase tracking-wider">{sigError}</p>
                        )}

                        <button
                            onClick={handleSaveSignature}
                            disabled={isSubmittingSignature || !signatureData || !signatureAccepted || (config?.registration_fields?.terms_accepted && !termsAccepted)}
                            style={{ background: 'linear-gradient(to right, #033f83, #079acf, #65bade)' }}
                            className="w-full text-white py-4 rounded-md font-black uppercase tracking-widest text-[11px] shadow-lg hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
                        >
                            {isSubmittingSignature ? 'PROCESANDO...' : 'GUARDAR FIRMA Y CONTINUAR'}
                        </button>
                    </div>
                </div>
            )}

            {showSalaModal && (
                <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/95 p-4 animate-fadeIn">
                    <div className="bg-slate-900 border border-white/10 rounded-[40px] p-8 max-w-md w-full space-y-6 shadow-2xl relative text-center">
                        <div className="flex justify-center mb-2">
                            <span className="text-5xl">🏢</span>
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-2xl font-black text-white italic uppercase tracking-tighter">Selección de Sala</h3>
                            <p className="text-white/60 text-xs leading-relaxed">
                                Como miembro del personal de Staff, por favor selecciona el salón o sala física en el que estarás registrando la asistencia de los participantes.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="relative">
                                <select
                                    className="w-full bg-white/5 border border-white/10 text-white p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all appearance-none"
                                    value={selectedSalaId}
                                    onChange={(e) => setSelectedSalaId(e.target.value)}
                                >
                                    <option value="" disabled className="bg-slate-900 text-white">Selecciona una sala...</option>
                                    {salas.map(s => (
                                        <option key={s.id} value={s.id} className="bg-slate-900 text-white">
                                            {s.name} {s.ubication ? `(${s.ubication})` : ''}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none">▼</div>
                            </div>
                        </div>

                        {salaError && (
                            <p className="text-red-400 text-[10px] font-bold uppercase tracking-wider">{salaError}</p>
                        )}

                        <button
                            onClick={handleSaveSala}
                            disabled={isSubmittingSala || !selectedSalaId}
                            style={{ background: 'linear-gradient(to right, #033f83, #079acf, #65bade)' }}
                            className="w-full text-white py-4 rounded-md font-black uppercase tracking-widest text-[11px] shadow-lg hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
                        >
                            {isSubmittingSala ? 'PROCESANDO...' : 'CONFIRMAR SALA'}
                        </button>
                    </div>
                </div>
            )}

            {/* Cardio Summit 2026 Official Footer */}
            {!hideGlobalFooter && (
                <footer className="w-full px-4 sm:px-6 py-6 flex-shrink-0 flex flex-col items-center gap-6 z-40 select-none bg-transparent max-w-4xl mx-auto">
                    {/* Tarjeta de Avales de Asociaciones Médicas idéntica al diseño */}
                    <div className="w-full flex justify-center">
                        <img
                            src="/assets/cardio/Logos_Asociaciones.png"
                            alt="CardioSummit 2026, un evento con el aval de: SCC, ASOCOLNEF, ACE"
                            className="w-full max-w-3xl h-auto object-contain drop-shadow-xl rounded-[28px] pointer-events-auto"
                        />
                    </div>

                    {/* Fila Legal con Logo Adium y texto oficial */}
                    <div className="w-full flex items-center justify-between gap-4">
                        <div className="flex flex-col items-center flex-shrink-0">
                            <img
                                src="/assets/cardio/CS_LogoAdium.png"
                                alt="Adium"
                                className="h-10 sm:h-12 w-auto object-contain drop-shadow"
                            />
                            <span className="text-[11px] font-bold text-white tracking-wider mt-1">Adium</span>
                        </div>
                        <div className="flex-1 text-[8px] sm:text-[9px] text-white/80 leading-snug text-left pl-2">
                            Este evento está dirigido exclusivamente al cuerpo médico y es de carácter personal e intransferible. Adium no promociona ni promueve el uso de sus productos / medicamentos en forma diferente al aprobado por la autoridad regulatoria e incluida en la información de prescripción o ficha técnica. Para mayor información comunicarse con el departamento médico de Adium S.A.S. Carrera 16 No. 85-96. Bogotá D.C. - Teléfono: +601 6460505. Si conoce un evento adverso de nuestros medicamentos, por favor reportarlo a farmacovigilancia@adium.com.co. CO--2600409
                        </div>
                    </div>
                </footer>
            )}
        </div>
    );
};

export default Layout;
